import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  LiteratureDocumentInfo,
  LiteratureTablePropertiesInfo,
  ModalService,
  RecordInfo,
  SimpleModalConfig,
} from '@frk/eds-components';
import { TranslateService } from '@shared/translate/translate.service';
import { Logger } from '@utils/logger';
import {
  LiteratureDocumentListingData,
  LiteratureDocumentListingState,
  ModalId,
  OrderHistoryList,
  LiteratureHistoryData,
  CartActionPerformed,
  OrderHistoryDetail,
  CartCheckoutMode,
} from '@types';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { IUserProfile, LoginSource } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { createDateComparator } from '@utils/sort-utils';
import { MyliteratureService } from './services/myliterature.service';

const logger = Logger.getLogger('LiteratureDashboardComponent');
@Component({
  selector: 'ft-literature-dashboard',
  templateUrl: './literature-dashboard.component.html',
  styleUrls: ['./literature-dashboard.component.scss'],
  providers: [MyliteratureService],
})
export class LiteratureDashboardComponent implements OnInit, OnDestroy {
  literatureCard: any = {};
  toggleButtons: any = [];
  literatureTableProperties: LiteratureTablePropertiesInfo;
  literatureDocumentData: Array<LiteratureDocumentInfo>;
  ModalId = ModalId;
  dialogData = [];
  apiDateFormat = 'YYYY-MM-DD';
  viewedData: LiteratureHistoryData;
  recentOrderHistoryListData: OrderHistoryDetail;
  profile$: Observable<IUserProfile>;
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private myliteratureService: MyliteratureService,
    private cartHandlerService: CartHandlerService,
    private changeDetectorRef: ChangeDetectorRef,
    private profileService: ProfileService,
    private modalService?: ModalService
  ) {}

  ngOnInit(): void {
    this.cartHandlerService
      .getCartItems()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((item: LiteratureDocumentListingState) => {
        if (
          CartActionPerformed.ADDED_FROM_LITERATURE_DASHBOARD ===
          item.cartAction
        ) {
          this.dialogData = [];
          this.dialogData = item.cartItems;
          this.openModal(ModalId.Dashboard_Cart_Modal);
          this.changeDetectorRef.detectChanges();
        }
      });
    this.literatureCard = {
      title: this.translateService.instant(
        'literature.lit-my-literature-title'
      ),
      subTitle: this.translateService.instant(
        'literature.lit-see-all-literature'
      ),
      ctaLink: this.myliteratureService.getLink(),
      ctaText: this.translateService.instant('literature.see-all'),
    };

    this.toggleButtons = [
      {
        active: true,
        text: this.translateService.instant('literature.viewed'),
      },
      {
        active: false,
        text: this.translateService.instant('literature.recently-ordered'),
      },
    ];

    this.cartHandlerService
      .getDeletedDocId()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.literatureDocumentData?.forEach((category) => {
          category?.records?.forEach((recordData) => {
            const index = data.findIndex((item) => item === recordData.docId);
            if (index > -1) {
              recordData.added = false;
            }
          });
        });
      });

    /**
     * Literature Card and Table Data
     */
    this.literatureTableProperties = {
      docType: this.translateService.instant('literature.type'),
      docName: this.translateService.instant(
        'literature.lit-listing-column-name'
      ),
      downloadTitle: this.translateService.instant('literature.download'),
      addToCartTitle: this.translateService.instant('literature.ADD_TO_CART'),
      defaultMessage: this.translateService.instant(
        'literature.dashboard-no-data-message'
      ),
      addedToCartTitle: this.translateService.instant(
        'literature.added-to-cart'
      ),
    };

    this.profile$ = this.myliteratureService.getProfileData$();

    this.profile$?.pipe(takeUntil(this.unsubscribe$)).subscribe((profile) => {
      this.literatureHistoryData(profile);
      this.orderHistoryData(profile);
    });
  }
  /**
   *
   * @param profile - profile details
   * @returns - literature history
   */
  literatureHistoryData(profile) {
    return this.myliteratureService
      .populateLiteratureHistory(profile.profileInfo)
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.viewedData = data;
        this.literatureDocumentData = this.myliteratureService.mapLiteratureHistory(
          data,
          null,
          5
        );
      });
  }
  /**
   *
   * @param profile - profile details
   * @returns - order history of documents
   */
  orderHistoryData(profile) {
    return this.myliteratureService
      .getRecentOrderHistoryList(profile.profileInfo)
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.recentOrderHistoryListData = data;
        this.literatureDocumentData = this.myliteratureService.mapLiteratureHistory(
          this.viewedData,
          data,
          5
        );
      });
  }
  /**
   *
   * @param orderDataList - list of all documents ordered
   * @returns - document object sorted in descending order of orderDate
   */
  sortOnDate(orderDataList: OrderHistoryList): OrderHistoryList {
    {
      const dateCompare = createDateComparator(this.apiDateFormat);
      orderDataList.orderHistDetail = orderDataList.orderHistDetail
        .sort((current, next) => {
          return dateCompare(
            current.orderDate,
            next.orderDate,
            null,
            null,
            false
          );
        })
        .reverse();
      return orderDataList;
    }
  }
  /**
   * Adds a document to the cart when button clicked
   * @param event - even is triggered on button click
   */
  addToCart(event: RecordInfo) {
    // to change the add to cart icon for same document in multiple tab
    this.literatureDocumentData.forEach((category) => {
      category.records.forEach((recordData) => {
        if (recordData.docId === event.docId) {
          recordData.added = true;
        }
      });
    });
    event.added = true;
    let literatureData;
    literatureData = this.myliteratureService.getLiteratureDocument(
      this.viewedData,
      this.recentOrderHistoryListData?.document,
      event
    );
    const cartData: LiteratureDocumentListingData = {
      literatureData,
    };
    this.cartHandlerService.addToCart(
      [cartData],
      CartActionPerformed.ADDED_FROM_LITERATURE_DASHBOARD
    );
  }
  /**
   * Download the document once button is clicked
   * @param event - even is triggered on button click
   */
  public downloadDocument(event) {
    this.myliteratureService.downloadDocument(event.downloadUrl);
    this.cartHandlerService.checkoutOrder(
      [{ literatureCode: event.docId, qty: 1 }],
      CartCheckoutMode.DOWNLOAD
    );
  }

  public openHistoryPage(event) {
    this.myliteratureService.navigateToUrl(event.ctaLink);
  }
  /**
   *
   * @param modelId modelId from enum
   * @param title optional if title required
   * opens dialog for dashboard cart
   */
  openModal(modelId: string, title?: string) {
    const config: SimpleModalConfig = {
      modalId: modelId,
      closeBtnLabel: 'Close',
      title: '',
      type: 'simple',
      body: '',
      themeTitle: 'dark',
      preTitle: '',
      eventName: 'lightbox_display',
      eventAction: 'Add to Cart',
    };
    this.modalService.open(modelId, config);
  }
  /**
   *
   * @param dialogId - dialogId that needs to be closed
   * closes the dailog
   */
  closeDialog(dialogId: string): void {
    this.modalService?.close(ModalId.Dashboard_Cart_Modal);
  }
  /**
   *
   * @param docId - doc need to deleted from add to cart modal
   * once user open the modal - same doc can be deleted also from modal
   * docId need to be deleted from local storage
   * call is getting deligated to cartHandler service to remove document from cart
   */
  updateStorage(docId: string) {
    this.cartHandlerService.removeFromCart([docId]);
    if (this.dialogData.length === 0) {
      this.closeDialog(ModalId.Dashboard_Cart_Modal);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
